import type { TranslationLanguage } from '../../ts/types';
import { MediaPermissionsStatus, PermissionState, MediaDeviceType } from '../types';
import {
  PERMISSION_STATE_TRANSLATION,
  type PermissionStateTranslation,
} from '../types/permission-messages';
import { isBrowserSupported, isFirefoxAndroid } from './browser-detection';

/**
 * Proactively checks camera and microphone permissions using the Permissions API
 * Falls back to getUserMedia testing if Permissions API is unavailable
 */
export async function checkMediaPermissions(
  forceFallback: boolean = false,
): Promise<MediaPermissionsStatus> {
  // Early return for unsupported browsers
  if (!isBrowserSupported()) {
    return {
      camera: PermissionState.UNKNOWN,
      microphone: PermissionState.UNKNOWN,
      supported: false,
    };
  }

  // Firefox Android has buggy Permissions API - use special handling
  if (isFirefoxAndroid()) {
    return firefoxAndroidPermissionCheck();
  }

  // Force fallback for immediate permission state after denial
  if (forceFallback) {
    return fallbackPermissionCheck();
  }

  // Try Permissions API first (more reliable when available)
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const [cameraResult, microphoneResult] = await Promise.allSettled([
        navigator.permissions.query({ name: MediaDeviceType.CAMERA as PermissionName }),
        navigator.permissions.query({ name: MediaDeviceType.MICROPHONE as PermissionName }),
      ]);

      return {
        camera:
          cameraResult.status === 'fulfilled'
            ? (cameraResult.value.state as PermissionState)
            : PermissionState.UNKNOWN,
        microphone:
          microphoneResult.status === 'fulfilled'
            ? (microphoneResult.value.state as PermissionState)
            : PermissionState.UNKNOWN,
        supported: true,
      };
    } catch (error) {
      // Permissions API failed, fall back to getUserMedia testing
      return await fallbackPermissionCheck();
    }
  }

  // Fallback for browsers without Permissions API
  return fallbackPermissionCheck();
}

/**
 * Checks camera permission specifically
 */
export const checkCameraPermission = async (): Promise<PermissionState> => {
  const { camera } = await checkMediaPermissions();
  return camera;
};

/**
 * Checks microphone permission specifically
 */
export const checkMicrophonePermission = async (): Promise<PermissionState> => {
  const { microphone } = await checkMediaPermissions();
  return microphone;
};

/**
 * Checks if both camera and microphone permissions are granted
 */
export const areMediaPermissionsGranted = async (): Promise<boolean> => {
  const { camera, microphone } = await checkMediaPermissions();
  return camera === PermissionState.GRANTED && microphone === PermissionState.GRANTED;
};

/**
 * Checks if user needs to be prompted for permissions
 */
export const needsPermissionPrompt = async (): Promise<Record<MediaDeviceType, boolean>> => {
  const { camera, microphone } = await checkMediaPermissions();
  return {
    [MediaDeviceType.CAMERA]:
      camera === PermissionState.PROMPT || camera === PermissionState.UNKNOWN,
    [MediaDeviceType.MICROPHONE]:
      microphone === PermissionState.PROMPT || microphone === PermissionState.UNKNOWN,
  };
};

/**
 * Firefox Android-specific permission checking
 * Firefox Android has buggy Permissions API that returns 'prompt' even after granting permissions
 * This function checks cache first, then returns PROMPT to show our custom modal
 */
const firefoxAndroidPermissionCheck = async (): Promise<MediaPermissionsStatus> => {
  const CACHE_KEY = 'firefox_android_permissions';
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  // Check cache first - if we have cached permissions, use them
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    try {
      const { permissions, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return permissions;
      }
    } catch (e) {
      // Invalid cache, remove it
      localStorage.removeItem(CACHE_KEY);
    }
  }

  // If no cached permissions, return PROMPT to show our custom modal
  // This preserves the permission modal flow while avoiding Firefox Android's buggy API
  return {
    camera: PermissionState.PROMPT,
    microphone: PermissionState.PROMPT,
    supported: true,
  };
};

/**
 * Firefox Android-specific permission test (called after user grants in our modal)
 * This function actually tests getUserMedia and caches the result
 */
export const testFirefoxAndroidPermissions = async (
  constraints: MediaStreamConstraints,
): Promise<MediaPermissionsStatus> => {
  const CACHE_KEY = 'firefox_android_permissions';

  const results: MediaPermissionsStatus = {
    camera: PermissionState.UNKNOWN,
    microphone: PermissionState.UNKNOWN,
    supported: true,
  };

  // Test the requested permissions
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    stream.getTracks().forEach((track) => {
      if (track.kind === 'video') {
        results.camera = PermissionState.GRANTED;
      } else if (track.kind === 'audio') {
        results.microphone = PermissionState.GRANTED;
      }
      track.stop();
    });

    // Cache the successful permissions
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        permissions: results,
        timestamp: Date.now(),
      }),
    );
  } catch (error: any) {
    // If permission was denied, mark appropriately
    if (constraints.video) {
      results.camera =
        error.name === 'NotAllowedError' ? PermissionState.DENIED : PermissionState.PROMPT;
    }
    if (constraints.audio) {
      results.microphone =
        error.name === 'NotAllowedError' ? PermissionState.DENIED : PermissionState.PROMPT;
    }
  }

  return results;
};

/**
 * Fallback permission checking using getUserMedia testing
 * This is less ideal as it may trigger permission prompts
 */
const fallbackPermissionCheck = async (): Promise<MediaPermissionsStatus> => {
  const results: MediaPermissionsStatus = {
    camera: PermissionState.UNKNOWN,
    microphone: PermissionState.UNKNOWN,
    supported: true,
  };

  // Helper function to handle permission testing
  const testPermission = async (constraints: MediaStreamConstraints, type: MediaDeviceType) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      stream.getTracks().forEach((track) => track.stop());
      results[type] = PermissionState.GRANTED;
    } catch (error: any) {
      const { name } = error;
      if (name === 'NotAllowedError' || name === 'NotFoundError') {
        results[type] = PermissionState.DENIED; // No device = effectively denied
      } else {
        results[type] = PermissionState.PROMPT;
      }
    }
  };

  // Test video permissions (minimal constraints to avoid prompts)
  await testPermission({ video: { width: 1, height: 1 }, audio: false }, MediaDeviceType.CAMERA);

  // Test audio permissions
  await testPermission({ video: false, audio: true }, MediaDeviceType.MICROPHONE);

  return results;
};

/**
 * Detects if permissions are completely blocked (not just denied) and cannot be requested again
 */
export const isPermissionBlocked = (permissionState: PermissionState): boolean =>
  permissionState === PermissionState.DENIED;

const resolvePermissionStrings = (languageCode?: string): PermissionStateTranslation => {
  if (!languageCode) return PERMISSION_STATE_TRANSLATION.en;
  return (
    PERMISSION_STATE_TRANSLATION[languageCode as TranslationLanguage] ??
    PERMISSION_STATE_TRANSLATION.en
  );
};

/**
 * Builds the localized "permission denied + how to reset" message shown
 * in the permission dialog. Returns an empty string if neither denial
 * applies (the dialog uses this to decide whether to render the block).
 */
export const getPermissionResetInstructions = (
  hasVideoDenied: boolean,
  hasAudioDenied: boolean,
  languageCode?: string,
): string => {
  const t = resolvePermissionStrings(languageCode);

  if (hasVideoDenied && hasAudioDenied) {
    return `${t.bothDenied} ${t.resetCameraAndMic}`;
  }
  if (hasVideoDenied) {
    return `${t.cameraDenied} ${t.resetCamera}`;
  }
  if (hasAudioDenied) {
    return `${t.microphoneDenied} ${t.resetMic}`;
  }
  return '';
};

/**
 * Localised Firefox-Android fallback warning. Rendered in the
 * permission dialog when the reset-instructions block is not shown but
 * the user is on Firefox Android (which has known media-capture
 * reliability issues on that platform combination).
 */
export const getFirefoxAndroidWarning = (languageCode?: string): string =>
  resolvePermissionStrings(languageCode).firefoxAndroidWarning;
